import io from "socket.io-client";
import { mapCommunicationError } from "../api/communicationApi.js";

export const resolveSocketUrl = () => {
  let url = process.env.REACT_APP_SOCKET_URL;
  if (!url || url === "undefined" || url.includes("localhost:5001") || url.includes("127.0.0.1:5001")) {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL.replace(/\/api\/?$/, "");
    }
    return "http://localhost:5000";
  }
  return url;
};

export class CommunicationSocketClient {
  constructor({
    socketUrl = resolveSocketUrl(),
    getToken = () => (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null),
    ioFactory = io,
    reconnectionAttempts = 10,
    autoConnect = true
  } = {}) {
    this.socketUrl = socketUrl;
    this.getToken = getToken;
    this.ioFactory = ioFactory;
    this.reconnectionAttempts = reconnectionAttempts;
    this.autoConnect = autoConnect;

    this.socket = null;
    this.connected = false;
    this.currentConversationId = null;

    // Internal listener registries to prevent duplicates
    this.listeners = {
      messageNew: new Set(),
      messageUpdated: new Set(),
      messageDeleted: new Set(),
      readUpdated: new Set(),
      connectionChange: new Set()
    };
  }

  connect() {
    if (this.socket && (this.socket.connected || this.socket.connecting)) {
      return this.socket;
    }

    const token = this.getToken();
    this.socket = this.ioFactory(this.socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
      reconnectionAttempts: this.reconnectionAttempts,
      autoConnect: this.autoConnect,
      auth: token ? { token } : {}
    });

    this.setupSocketHandlers();
    return this.socket;
  }

  setupSocketHandlers() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      this.connected = true;
      this.notifyConnection(true);

      // Reconnect invariant: If a conversation was open before disconnect, rejoin it
      if (this.currentConversationId) {
        this.joinConversation(this.currentConversationId).catch(() => {
          // Failure to rejoin on reconnect handled gracefully
        });
      }
    });

    this.socket.on("disconnect", () => {
      this.connected = false;
      this.notifyConnection(false);
    });

    this.socket.on("connect_error", err => {
      this.connected = false;
      this.notifyConnection(false, mapCommunicationError(err));
    });

    // Realtime broadcast events from server
    this.socket.on("communication:message:new", message => {
      this.listeners.messageNew.forEach(cb => {
        try {
          cb(message);
        } catch (e) {
          console.error("Error in messageNew listener", e);
        }
      });
    });

    this.socket.on("communication:message:updated", message => {
      this.listeners.messageUpdated.forEach(cb => {
        try {
          cb(message);
        } catch (e) {
          console.error("Error in messageUpdated listener", e);
        }
      });
    });

    this.socket.on("communication:message:deleted", message => {
      this.listeners.messageDeleted.forEach(cb => {
        try {
          cb(message);
        } catch (e) {
          console.error("Error in messageDeleted listener", e);
        }
      });
    });

    this.socket.on("communication:read:updated", data => {
      this.listeners.readUpdated.forEach(cb => {
        try {
          cb(data);
        } catch (e) {
          console.error("Error in readUpdated listener", e);
        }
      });
    });
  }

  notifyConnection(isConnected, error = null) {
    this.listeners.connectionChange.forEach(cb => {
      try {
        cb({ isConnected, error });
      } catch (e) {
        console.error("Error in connectionChange listener", e);
      }
    });
  }

  disconnect() {
    if (this.socket) {
      if (this.currentConversationId) {
        this.leaveConversation(this.currentConversationId).catch(() => {});
      }
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.currentConversationId = null;
      this.notifyConnection(false);
    }
  }

  // --- Subscriptions ---
  onMessageNew(cb) {
    this.listeners.messageNew.add(cb);
    return () => this.listeners.messageNew.delete(cb);
  }

  onMessageUpdated(cb) {
    this.listeners.messageUpdated.add(cb);
    return () => this.listeners.messageUpdated.delete(cb);
  }

  onMessageDeleted(cb) {
    this.listeners.messageDeleted.add(cb);
    return () => this.listeners.messageDeleted.delete(cb);
  }

  onReadUpdated(cb) {
    this.listeners.readUpdated.add(cb);
    return () => this.listeners.readUpdated.delete(cb);
  }

  onConnectionChange(cb) {
    this.listeners.connectionChange.add(cb);
    return () => this.listeners.connectionChange.delete(cb);
  }

  // --- Room Lifecycle ---
  async joinConversation(conversationId) {
    if (!conversationId) throw new Error("conversationId is required");
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      this.socket.emit(
        "communication:conversation:join",
        { conversationId },
        ack => {
          if (!ack) {
            return reject(new Error("Timeout or no response from server"));
          }
          if (!ack.ok) {
            const safeMsg = mapCommunicationError(ack.error);
            const err = new Error(safeMsg);
            err.code = ack.error?.code;
            return reject(err);
          }
          this.currentConversationId = conversationId;
          resolve(ack);
        }
      );
    });
  }

  async leaveConversation(conversationId) {
    if (!conversationId) return;
    if (!this.socket || !this.socket.connected) {
      this.currentConversationId = null;
      return;
    }

    return new Promise(resolve => {
      this.socket.emit(
        "communication:conversation:leave",
        { conversationId },
        ack => {
          if (this.currentConversationId === conversationId) {
            this.currentConversationId = null;
          }
          resolve(ack || { ok: true });
        }
      );
    });
  }

  // --- Operations with Acknowledgements ---
  async sendMessage({ conversationId, idempotencyKey, text }) {
    if (!conversationId) throw new Error("conversationId is required");
    if (!idempotencyKey) throw new Error("idempotencyKey is required");
    if (!text || !text.trim()) throw new Error("text is required");
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      this.socket.emit(
        "communication:message:send",
        {
          conversationId,
          idempotencyKey,
          payload: { text: text.trim() }
        },
        ack => {
          if (!ack) {
            return reject(new Error("No response from server"));
          }
          if (!ack.ok) {
            const safeMsg = mapCommunicationError(ack.error);
            const err = new Error(safeMsg);
            err.code = ack.error?.code;
            return reject(err);
          }
          resolve(ack.message);
        }
      );
    });
  }

  async editMessage({ conversationId, messageId, text }) {
    if (!conversationId || !messageId) {
      throw new Error("conversationId and messageId are required");
    }
    if (!text || !text.trim()) throw new Error("text is required");
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      this.socket.emit(
        "communication:message:edit",
        {
          conversationId,
          messageId,
          payload: { text: text.trim() }
        },
        ack => {
          if (!ack) {
            return reject(new Error("No response from server"));
          }
          if (!ack.ok) {
            const safeMsg = mapCommunicationError(ack.error);
            const err = new Error(safeMsg);
            err.code = ack.error?.code;
            return reject(err);
          }
          resolve(ack.message);
        }
      );
    });
  }

  async deleteMessage({ conversationId, messageId }) {
    if (!conversationId || !messageId) {
      throw new Error("conversationId and messageId are required");
    }
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      this.socket.emit(
        "communication:message:delete",
        {
          conversationId,
          messageId
        },
        ack => {
          if (!ack) {
            return reject(new Error("No response from server"));
          }
          if (!ack.ok) {
            const safeMsg = mapCommunicationError(ack.error);
            const err = new Error(safeMsg);
            err.code = ack.error?.code;
            return reject(err);
          }
          resolve(ack.message);
        }
      );
    });
  }

  async markRead({ conversationId, messageId = null }) {
    if (!conversationId) throw new Error("conversationId is required");
    this.ensureConnected();

    return new Promise((resolve, reject) => {
      this.socket.emit(
        "communication:conversation:read",
        {
          conversationId,
          messageId
        },
        ack => {
          if (!ack) {
            return resolve(null);
          }
          if (!ack.ok) {
            return resolve(null); // non-critical
          }
          resolve(ack.membership);
        }
      );
    });
  }

  ensureConnected() {
    if (!this.socket) {
      this.connect();
    }
  }
}

export const communicationSocket = new CommunicationSocketClient();
